import { create } from 'zustand';
import { supabase } from '../lib/supabase';
import { createRealtimeSync, type RealtimeSync } from '../lib/realtime/createRealtimeSync';
import { logResyncFailure } from '../lib/realtime/logResync';
import {
  Notification,
  NotificationCategory,
  NotificationLevel,
  NotificationPriority,
  NotificationTemplate,
  NotificationType,
  NotificationTypeSetting,
} from '../types/notifications';
import type { Database, Json } from '../types/supabase';

type NotificationRow = Database['public']['Tables']['notifications']['Row'];
type NotificationTemplateRow = Database['public']['Tables']['notification_templates']['Row'];
type NotificationTypeRegistryRow = Database['public']['Tables']['notification_type_registry']['Row'];
type NotificationPreferenceRow = Database['public']['Tables']['notification_preferences']['Row'];

/** Narrow a nullable JSON column into a plain domain Record. */
function toRecord(value: Json | null | undefined): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

/**
 * Normalize a `notifications` DB row into the domain `Notification`.
 * The DB keeps `user_id`/`read` nullable and `type`/`category`/`priority` as
 * free-form strings; they are narrowed here to the canonical unions.
 */
function toNotification(row: NotificationRow): Notification {
  return {
    id: row.id,
    user_id: row.user_id ?? '',
    type: row.type as NotificationType,
    category: (row.category as NotificationCategory | null) ?? undefined,
    title: row.title,
    content: row.content,
    icon: row.icon ?? undefined,
    link: row.link ?? undefined,
    priority: (row.priority as NotificationPriority | null) ?? undefined,
    data: toRecord(row.data),
    read: row.read ?? false,
    created_at: row.created_at ?? '',
    // Défaut permissif : une notification écrite avant l'introduction de la
    // colonne (28/07/2026) reste interruptive, on ne masque rien par accident.
    push_eligible: row.push_eligible ?? true,
  };
}

/** Normalize a `notification_templates` DB row into `NotificationTemplate`. */
function toNotificationTemplate(row: NotificationTemplateRow): NotificationTemplate {
  return {
    id: row.id,
    type: row.type as NotificationType,
    title: row.title,
    content: row.content,
    variables: Array.isArray(row.variables) ? (row.variables as string[]) : [],
    enabled: row.enabled ?? false,
    created_at: row.created_at ?? '',
    updated_at: row.updated_at ?? '',
  };
}

/**
 * Croise le registre des types et les préférences de l'utilisateur pour donner
 * le réglage EFFECTIF de chaque type. Le front ne recalcule pas la règle : il
 * lit la même source que la porte SQL (`apply_notification_preferences`), donc
 * ce qu'il affiche est exactement ce qui sera appliqué à l'écriture.
 */
function toTypeSettings(
  registry: NotificationTypeRegistryRow[],
  prefs: NotificationPreferenceRow[],
): NotificationTypeSetting[] {
  const byType = new Map(prefs.map((p) => [p.type, p.level as NotificationLevel]));
  return registry.map((entry) => {
    const explicit = byType.get(entry.type);
    return {
      type: entry.type,
      label: entry.label,
      category: entry.category as NotificationCategory,
      description: entry.description,
      default_level: entry.default_level as NotificationLevel,
      sort_order: entry.sort_order,
      level: explicit ?? (entry.default_level as NotificationLevel),
      isExplicit: explicit !== undefined,
    };
  });
}

/**
 * Taille du fil chargé en mémoire. Volontairement sous le plafond `max_rows`
 * de PostgREST (1000) : au-delà, la troncature est SILENCIEUSE et on croirait
 * afficher tout l'historique. La cloche en montre 10, le centre filtre dessus ;
 * l'historique complet reste en base.
 */
const NOTIFICATION_FEED_LIMIT = 500;

/** Live subscription + last user, so incoming notifications surface without refresh. */
let notificationsSync: RealtimeSync | null = null;
let lastNotifUserId: string | null = null;

interface NotificationState {
  notifications: Notification[];
  templates: NotificationTemplate[];
  /** Réglage effectif de chaque type pour l'utilisateur courant. */
  typeSettings: NotificationTypeSetting[];
  unreadCount: number;
  loading: boolean;
  savingType: string | null;
  error: string | null;
  activeToasts: Notification[];

  // Fetch data
  fetchNotifications: (userId: string, opts?: { silent?: boolean }) => Promise<void>;
  fetchTemplates: (opts?: { silent?: boolean }) => Promise<void>;
  fetchTypeSettings: (userId: string) => Promise<void>;

  // Notification actions
  markAsRead: (notificationId: string) => Promise<void>;
  markAllAsRead: (userId: string) => Promise<void>;
  deleteNotification: (notificationId: string) => Promise<void>;

  // Toast management
  addToast: (notification: Notification) => void;
  removeToast: (notificationId: string) => void;

  // Template management
  updateTemplate: (templateId: string, updates: Partial<NotificationTemplate>) => Promise<void>;

  // Preferences management
  setTypeLevel: (userId: string, type: string, level: NotificationLevel) => Promise<void>;
  setCategoryLevel: (userId: string, category: NotificationCategory, level: NotificationLevel) => Promise<void>;
}

/** Lazily open the notifications Realtime subscription (idempotent). */
function ensureNotificationsRealtime(get: () => NotificationState): void {
  if (!notificationsSync) {
    notificationsSync = createRealtimeSync({
      channelName: 'rt-notifications',
      tables: ['notifications', 'notification_templates'],
      onChange: async () => {
        if (lastNotifUserId) await get().fetchNotifications(lastNotifUserId, { silent: true });
        await get().fetchTemplates({ silent: true });
      },
    });
  }
  notificationsSync.subscribe();
}

export const useNotificationStore = create<NotificationState>((set, get) => ({
  notifications: [],
  templates: [],
  typeSettings: [],
  unreadCount: 0,
  loading: false,
  savingType: null,
  error: null,
  activeToasts: [],

  fetchNotifications: async (userId: string, opts) => {
    const silent = opts?.silent ?? false;
    lastNotifUserId = userId;
    if (!silent) set({ loading: true, error: null });
    try {
      // Fenêtre BORNÉE, et compteur de non-lues compté côté serveur.
      //
      // Le `select('*')` sans limite tombait dans le piège `max_rows=1000` de
      // PostgREST (ENGINEERING.md) : au-delà, la réponse est tronquée SANS
      // erreur. Gilles avait 1 258 lignes et n'en voyait que 1 000 — et surtout
      // la pastille de non-lues se calculait sur ce jeu tronqué, donc elle
      // pouvait mentir sans que rien ne le signale. On borne explicitement le
      // fil affiché et on demande le compte exact au serveur.
      const [listRes, unreadRes] = await Promise.all([
        supabase
          .from('notifications')
          .select('*')
          .eq('user_id', userId)
          .order('created_at', { ascending: false })
          .limit(NOTIFICATION_FEED_LIMIT),
        supabase
          .from('notifications')
          .select('id', { count: 'exact', head: true })
          .eq('user_id', userId)
          .eq('read', false),
      ]);

      if (listRes.error) throw listRes.error;
      if (unreadRes.error) throw unreadRes.error;

      const notifications = (listRes.data ?? []).map(toNotification);
      set({
        notifications,
        unreadCount: unreadRes.count ?? notifications.filter(n => !n.read).length,
      });
      ensureNotificationsRealtime(get);
    } catch (error: unknown) {
      if (silent) logResyncFailure('notifications resync failed', error);
      else set({ error: error instanceof Error ? error.message : String(error) });
    } finally {
      if (!silent) set({ loading: false });
    }
  },

  fetchTemplates: async (opts) => {
    const silent = opts?.silent ?? false;
    if (!silent) set({ loading: true, error: null });
    try {
      const { data, error } = await supabase
        .from('notification_templates')
        .select('*')
        .order('type');

      if (error) throw error;

      set({ templates: (data ?? []).map(toNotificationTemplate) });
    } catch (error: unknown) {
      if (silent) logResyncFailure('notification-templates resync failed', error);
      else set({ error: error instanceof Error ? error.message : String(error) });
    } finally {
      if (!silent) set({ loading: false });
    }
  },

  fetchTypeSettings: async (userId: string) => {
    set({ loading: true, error: null });
    try {
      const [registryRes, prefsRes] = await Promise.all([
        supabase
          .from('notification_type_registry')
          .select('*')
          .order('sort_order'),
        supabase
          .from('notification_preferences')
          .select('*')
          .eq('user_id', userId),
      ]);

      if (registryRes.error) throw registryRes.error;
      if (prefsRes.error) throw prefsRes.error;

      set({ typeSettings: toTypeSettings(registryRes.data ?? [], prefsRes.data ?? []) });
    } catch (error: unknown) {
      set({ error: error instanceof Error ? error.message : String(error) });
    } finally {
      set({ loading: false });
    }
  },

  markAsRead: async (notificationId: string) => {
    try {
      const { error } = await supabase
        .from('notifications')
        .update({ read: true })
        .eq('id', notificationId);

      if (error) throw error;

      set(state => ({
        notifications: state.notifications.map(n => 
          n.id === notificationId ? { ...n, read: true } : n
        ),
        unreadCount: state.unreadCount - 1
      }));
    } catch (error: unknown) {
      set({ error: error instanceof Error ? error.message : String(error) });
    }
  },

  markAllAsRead: async (userId: string) => {
    try {
      const { error } = await supabase
        .from('notifications')
        .update({ read: true })
        .eq('user_id', userId)
        .eq('read', false);

      if (error) throw error;

      set(state => ({
        notifications: state.notifications.map(n => ({ ...n, read: true })),
        unreadCount: 0
      }));
    } catch (error: unknown) {
      set({ error: error instanceof Error ? error.message : String(error) });
    }
  },

  deleteNotification: async (notificationId: string) => {
    try {
      const { error } = await supabase
        .from('notifications')
        .delete()
        .eq('id', notificationId);

      if (error) throw error;

      set(state => ({
        notifications: state.notifications.filter(n => n.id !== notificationId),
        unreadCount: state.notifications.filter(n => !n.read && n.id !== notificationId).length,
        activeToasts: state.activeToasts.filter(n => n.id !== notificationId)
      }));
    } catch (error: unknown) {
      set({ error: error instanceof Error ? error.message : String(error) });
    }
  },

  addToast: (notification: Notification) => {
    set(state => {
      // Ne pas ajouter si le toast existe déjà
      if (state.activeToasts.some(t => t.id === notification.id)) {
        return state;
      }
      // Limiter à 3 toasts maximum
      const newToasts = [notification, ...state.activeToasts].slice(0, 3);
      return { activeToasts: newToasts };
    });
  },

  removeToast: (notificationId: string) => {
    set(state => ({
      activeToasts: state.activeToasts.filter(n => n.id !== notificationId)
    }));
  },

  updateTemplate: async (templateId: string, updates: Partial<NotificationTemplate>) => {
    try {
      const { error } = await supabase
        .from('notification_templates')
        .update({ ...updates, updated_at: new Date().toISOString() })
        .eq('id', templateId);

      if (error) throw error;

      set(state => ({
        templates: state.templates.map(t => 
          t.id === templateId ? { ...t, ...updates } : t
        )
      }));
    } catch (error: unknown) {
      set({ error: error instanceof Error ? error.message : String(error) });
    }
  },

  /**
   * Règle un type pour l'utilisateur courant. Optimiste : le bouton répond
   * immédiatement, et on remet l'ancienne valeur si l'écriture échoue — un
   * réglage qui a l'air pris mais qui ne l'est pas, c'est pire que pas de
   * réglage du tout.
   */
  setTypeLevel: async (userId: string, type: string, level: NotificationLevel) => {
    const previous = get().typeSettings;
    set({
      savingType: type,
      error: null,
      typeSettings: previous.map((s) => (s.type === type ? { ...s, level, isExplicit: true } : s)),
    });

    const { error } = await supabase
      .from('notification_preferences')
      .upsert(
        { user_id: userId, type, level, updated_at: new Date().toISOString() },
        { onConflict: 'user_id,type' },
      );

    if (error) {
      set({ typeSettings: previous, error: error.message });
    }
    set({ savingType: null });
  },

  /** Applique le même niveau à tous les types d'une catégorie, en un geste. */
  setCategoryLevel: async (userId: string, category: NotificationCategory, level: NotificationLevel) => {
    const previous = get().typeSettings;
    const targets = previous.filter((s) => s.category === category);
    if (targets.length === 0) return;

    set({
      savingType: `category:${category}`,
      error: null,
      typeSettings: previous.map((s) => (s.category === category ? { ...s, level, isExplicit: true } : s)),
    });

    const { error } = await supabase
      .from('notification_preferences')
      .upsert(
        targets.map((s) => ({
          user_id: userId,
          type: s.type,
          level,
          updated_at: new Date().toISOString(),
        })),
        { onConflict: 'user_id,type' },
      );

    if (error) {
      set({ typeSettings: previous, error: error.message });
    }
    set({ savingType: null });
  },
}));
